import { useMemo, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import { failureError, invalidError } from '@/components/pbo/notice'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import { MENU_TYPE } from '@/services/auth/consts'
import { menuQueryKeys, useGetMenuList, type MenuParams, type MenuRow } from './queries/useGetMenuList'
import { useDeleteMenus, useSaveMenus } from './queries/useMenuMutations'
import { invalidReason, type MenuIndex } from './validate'
import { MENU_TREE_ROOT, MESSAGES } from './consts'
import MenuManagementView, { type ActionCtx, type CellOption, type Row, type Wiring } from './MenuManagementView'

/**
 * 메뉴관리(SY11) — 1.0 SY02 를 2.0 으로 옮긴 화면(LOGISPRD-16042, BE LOGISPRD-16595). 1.0 SY02 와 병행 운영하려고 새 메뉴코드 SY11 을 쓴다.
 *
 * 공통코드(sy10) 화면과 같은 배선 규약이다 — [추가]는 빈 행을 맨 위에, [저장]은 새 행 POST·기존 행 PUT, [삭제]는 DELETE 를 행별 순차로 보낸다.
 * 미저장 새 행 삭제는 화면에서만 지운다. 저장 뒤 좌측 메뉴는 다시 읽지 않는다 — 새 메뉴는 모든 권한에 MENU_ACT='N' 으로 생겨
 * 권한관리에서 켜기 전까지 안 보이고, 이름·순서 변경은 재로그인 때 반영된다.
 */
/** 표 행 — key = BE 행 id(문자열). _id 없음 = [추가]로 생긴 미저장 행. __new = DS 표 계약(필수 미입력 표기) + 메뉴코드 입력칸 노출. */
type EditableRow = Omit<MenuRow, '_id'> & { _id?: number } & Row & { key: string; __new?: boolean }
type SavedRow = EditableRow & { _id: number }

const toEditable = (row: MenuRow): EditableRow => ({ ...row, key: String(row._id) })

const toParams = (filters: Record<string, unknown>): MenuParams => {
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  return { menuCode: text(filters.MENU_CD), menuName: text(filters.MENU_NM) }
}

/** BE 문구(apiErrorText) 우선, 빈 body 응답은 HTTP 상태를 함께 노출 — 공통코드 화면 규약. */
const apiErrMessage = (e: unknown) => {
  const status = (e as { response?: { status?: number } })?.response?.status
  return apiErrorText(
    e,
    status ? `요청을 처리하지 못했습니다(HTTP ${status}). 잠시 후 다시 시도하세요.` : '요청을 처리하지 못했습니다. 잠시 후 다시 시도하세요.',
  )
}

/** 순차 저장·삭제 훅이 실패 오류에 싣는 앞선 반영 건수 */
const doneOf = (e: unknown) => (e as { done?: number })?.done ?? 0
const rowKeyOf = (r: Row) => String((r.key as string | number | undefined) ?? '')
const sameParams = (a: MenuParams, b: MenuParams) => JSON.stringify(a) === JSON.stringify(b)
const NO_ROWS: MenuRow[] = []

function MenuManagement() {
  const [params, setParams] = useState<MenuParams>({})
  const { data: fetchedRows = NO_ROWS, isFetching } = useGetMenuList(params)
  // 상위메뉴 선택지는 조회 조건과 상관없이 전체 메뉴 중 root 아래 FOLD 로 만든다(2단 트리 — validate.ts) — 메뉴명으로 좁혀 조회하면 부모 폴더가 결과에서 빠져 기존 행을 저장할 수 없게 된다.
  //  조건 없는 조회와 같은 키라 진입·[초기화] 때는 요청이 한 번만 나간다.
  const { data: allRows = NO_ROWS } = useGetMenuList({})
  const saveMenus = useSaveMenus()
  const deleteMenus = useDeleteMenus()
  const queryClient = useQueryClient()

  const menuTreeOptions = useMemo<CellOption[]>(
    () => [
      { value: MENU_TREE_ROOT, label: MENU_TREE_ROOT },
      ...allRows
        .filter((r) => r.MENU_TYP === MENU_TYPE.FOLD && r.MENU_TREE === MENU_TREE_ROOT)
        .map((r) => ({ value: r.MENU_CD, label: `${r.MENU_CD} ${r.MENU_NM}` })),
    ],
    [allRows],
  )
  const parentCodes = useMemo(() => new Set(menuTreeOptions.map((o) => o.value)), [menuTreeOptions])
  /** 하위 메뉴가 있는 메뉴코드 — 1.0 메뉴 트리는 PROG 의 하위를 펼치지 않아(services/auth/apis.ts:49) 이 폴더는 FOLD 로 둔다. */
  const menuIndex = useMemo<MenuIndex>(
    () => ({
      parentCodes,
      folderCodesWithChildren: new Set(allRows.map((r) => r.MENU_TREE)),
      savedCodes: new Set(allRows.map((r) => r.MENU_CD.toUpperCase())),
    }),
    [parentCodes, allRows],
  )

  const [rows, setRows] = useState<EditableRow[]>([])
  const newSeqRef = useRef(0)
  useEffect(() => {
    // 저장 뒤 재조회가 [추가]한 미저장 행을 지우지 않게 앞에 남긴다. [조회]·[초기화]는 그 전에 걷는다.
    setRows((prev) => [...prev.filter((r) => r._id == null), ...fetchedRows.map(toEditable)])
  }, [fetchedRows])

  /** 저장 대상 — 선택 행을 편집 반영본에서 되찾는다(뷰가 넘긴 행은 표시용 사본이다). */
  const selectedRows = (ctx: ActionCtx): EditableRow[] => {
    const picked = new Set(ctx.selected.map(rowKeyOf))
    return rows.filter((r) => picked.has(r.key))
  }

  /** [조회]·[초기화] — 같은 조건이면 캐시라 요청이 안 나가 그때만 무효화하고 편집을 버린다(1.0). 미저장 새 행은 걷는다. */
  const search = async (filters: Record<string, unknown>) => {
    const next = toParams(filters)
    const same = sameParams(next, params)
    setRows(same ? fetchedRows.map(toEditable) : [])
    setParams(next)
    if (same) await queryClient.invalidateQueries({ queryKey: menuQueryKeys.list(next) })
  }

  const wiring: Wiring = {
    columnLayout: { sy11Table: coreTableLayoutSlot('SY11_HEAD') },
    rows: { ROWS: () => ({ rows, loading: isFetching }) },
    menuTreeOptions,
    actions: {
      search,
      /** 편집 셀 — 바뀐 행만 갈아 끼운다. 기존 행의 메뉴코드는 붙여넣기로 들어와도 버린다(PUT 이 menuCode 를 받지 않는다). */
      ROWS_rowsChange: (changed) => {
        const byKey = new Map(changed.map((r) => [rowKeyOf(r), r]))
        setRows((prev) =>
          prev.map((r) => {
            const c = byKey.get(r.key)
            if (!c) return r
            return { ...r, ...c, MENU_CD: r._id == null ? c.MENU_CD : r.MENU_CD } as EditableRow
          }),
        )
      },
      sy11AddButton: () => {
        newSeqRef.current += 1
        setRows((prev) => [
          { MENU_CD: '', MENU_NM: '', MENU_TYP: '', MENU_TREE: '', MENU_ORD: '', key: `new-${newSeqRef.current}`, __new: true },
          ...prev,
        ])
      },
      /** [저장] 확인 — 검증 실패·API 실패 모두 throw(확인창·선택 유지). 실패 지점에서 멈추므로 앞서 등록된 새 행은 미저장 목록에서 뺀다(다시 저장하면 중복 400). */
      sy11SaveConfirm_ok: async (ctx) => {
        const targets = selectedRows(ctx)
        if (targets.length === 0) throw invalidError(MESSAGES.noSelection)
        const reason = invalidReason(targets, menuIndex)
        if (reason) throw invalidError(reason)
        const dropPosted = (done: number) => {
          const posted = new Set(
            targets
              .slice(0, done)
              .filter((r) => r._id == null)
              .map((r) => r.key),
          )
          setRows((prev) => prev.filter((r) => !posted.has(r.key)))
        }
        try {
          await saveMenus.mutateAsync(targets)
          dropPosted(targets.length)
          // 저장 후 조회조건 유지 — 서버 반영값(새 행 id·새 FOLD 선택지)만 다시 읽는다.
          await queryClient.invalidateQueries({ queryKey: menuQueryKeys.all })
        } catch (e) {
          const done = doneOf(e)
          dropPosted(done)
          throw failureError(done > 0 ? MESSAGES.partialFailed(targets.length, done, apiErrMessage(e)) : apiErrMessage(e))
        }
      },
      /** [삭제] 확인 — 미저장 새 행은 화면에서만 지우고, 저장된 행은 행별 DELETE 한다. 하위 메뉴가 있는 폴더는 BE 가 400 이라 보내기 전에 막는다. */
      sy11DeleteConfirm_ok: async (ctx) => {
        const targets = selectedRows(ctx)
        if (targets.some((r) => r._id != null && menuIndex.folderCodesWithChildren.has(r.MENU_CD)))
          throw invalidError(MESSAGES.deleteFolderHasChildren)
        const local = new Set(targets.filter((r) => r._id == null).map((r) => r.key))
        setRows((prev) => prev.filter((r) => !local.has(r.key)))
        const ids = targets.filter((r): r is SavedRow => r._id != null).map((r) => r._id)
        if (ids.length === 0) return
        try {
          await deleteMenus.mutateAsync(ids)
          await queryClient.invalidateQueries({ queryKey: menuQueryKeys.all })
        } catch (e) {
          const done = doneOf(e)
          const deleted = new Set(ids.slice(0, done))
          setRows((prev) => prev.filter((r) => r._id == null || !deleted.has(r._id)))
          // 확인창의 건수(선택 행 전체)와 맞춘다 — 화면에서만 지운 미저장 행도 반영 건수에 넣는다
          const removed = local.size + done
          throw failureError(removed > 0 ? MESSAGES.partialFailed(targets.length, removed, apiErrMessage(e)) : apiErrMessage(e))
        }
      },
    },
    useBusy: () => saveMenus.isPending || deleteMenus.isPending,
    onReset: () => {
      search({}).catch(() => undefined)
    },
  }

  return <MenuManagementView wiring={wiring} />
}

export default MenuManagement
